import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";

import Button from "components/Button";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import SearchInput from "components/SearchInput";

import PortfolioCard from "../PortfolioCard";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { SILENT } from "store/silent";
import { getCompanyRequest } from "store/slices/companiesSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import {
  createPortfolioRequest,
  getPortfolioListRequest,
  resetSuccessfullyAddedPortfolio,
} from "store/slices/portfolioSlice";

import { ModalType } from "entities/common";
import { CreatePortfolioRequest, PortfolioItem } from "entities/portfolios";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";
import { PortfolioFormValues } from "widgets/Modal/CreatePortfolio/Content";

import { showToast } from "store/slices/toastSlice";
import styles from "./style.module.scss";

const buildTabUrl = (companyId: string, search: string, page: number): string =>
  `${PAGES.COMPANY_DETAIL.replace(":id", companyId)}${QueryHelper.getQueryString({
    [QUERY_PARAMS.TAB]: "portfolios",
    [QUERY_PARAMS.SEARCH]: search.trim() || undefined,
    [QUERY_PARAMS.PAGE]: page > 1 ? page : undefined,
  })}`;

const CompanyPortfoliosTab = () => {
  const callbackKey = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: companyId } = useParams();
  const [search, setSearch] = useState(
    () => new URLSearchParams(location.search).get(QUERY_PARAMS.SEARCH) || "",
  );
  const [page, setPage] = useState(() => Number(new URLSearchParams(location.search).get(QUERY_PARAMS.PAGE)) || 1);
  const debouncedSearch = useDebounce(search, 500);
  const dispatch = useAppDispatch();
  const {
    items: portfolios,
    isLoading,
    meta,
    successfullyAddedId,
  } = useAppSelector((state: RootStore) => state.portfolios);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
  }, []);

  const handleSavePortfolio = useCallback(
    (values?: unknown) => {
      const data = values as PortfolioFormValues;

      const body: CreatePortfolioRequest = {
        portfolio_name: data.name,
        company_id: Number(companyId),
        description: data.description,
        city: data.city,
        area: data.area,
        building_name: data.buildingName,
        unit_number: data.unitNumber,
        street: data.street,
        manager_id: Number(data.managerId),
      };

      dispatch(createPortfolioRequest({ body, images: data.images }));
    },
    [companyId, dispatch],
  );

  const handlePortfolioClick = useCallback(
    (portfolio: PortfolioItem) => () => {
      navigate(PAGES.PORTFOLIO_DETAIL.replace(":id", portfolio.id.toString()), {
        state: { from: buildTabUrl(companyId || "", search, page) },
      });
    },
    [navigate, companyId, search, page],
  );

  const handleCreatePortfolio = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSavePortfolio });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_PORTFOLIO,
        modalProps: { mode: "create", companyId: Number(companyId), callbackKey, isShowCompanySelect: false },
      }),
    );
  }, [dispatch, companyId, callbackKey, handleSavePortfolio]);

  const handleRequestPortfolios = useCallback(
    (debouncedSearch: string, page: number, companyId?: string, silent = false) => {
      dispatch(
        getPortfolioListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(),
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.COMPANY_ID]: Number(companyId),
            [QUERY_PARAMS.PAGE_SIZE]: 12,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch],
  );

  const totalPages = useMemo(() => {
    return Math.ceil(meta.total / meta.page_size);
  }, [meta.total, meta.page_size]);

  // keep the URL in sync so the state can be restored when returning from a portfolio
  useEffect(() => {
    if (!companyId) return;
    navigate(buildTabUrl(companyId, debouncedSearch, page), { replace: true });
  }, [navigate, companyId, debouncedSearch, page]);

  // request portfolios when search, page, or company filter change
  useEffect(() => {
    handleRequestPortfolios(debouncedSearch, page, companyId);
  }, [handleRequestPortfolios, debouncedSearch, page, companyId, successfullyAddedId]);

  const reload = useCallback(() => {
    handleRequestPortfolios(debouncedSearch, page, companyId, true);
  }, [handleRequestPortfolios, debouncedSearch, page, companyId]);

  useRealtime(["compliance", "incidents"], reload, companyId ? { companyId: Number(companyId) } : undefined);

  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(resetSuccessfullyAddedPortfolio());
      if (companyId) dispatch(getCompanyRequest(Number(companyId), SILENT));
      dispatch(
        showToast({
          id: "successfully-added-portfolio",
          message: "The portfolio was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
      dispatch(hideModal());
    }
  }, [successfullyAddedId, companyId, dispatch]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <SearchInput
          name="search"
          value={search}
          onChange={handleSearchChange}
          className={styles["search-input"]}
          placeholder="Search portfolios by name, description or location..."
        />

        <Button variant="primary" className={styles["header-create"]} onClick={handleCreatePortfolio}>
          <span className={styles["header-create-text"]}>Add portfolio</span>
          <span className={styles["header-create-icon"]}>
            <PlusIcon />
          </span>
        </Button>
      </div>

      <div className={styles.portfolios}>
        {isLoading ? (
          <Loader isLoading inline />
        ) : (
          portfolios.map((item, index) => (
            <PortfolioCard key={`portfolio-${index}-${item?.id}`} data={item} onClick={handlePortfolioClick(item)} />
          ))
        )}

        {portfolios?.length === 0 && !isLoading && (
          <div className={styles["not-found"]}>
            <NotFoundIcon />

            <p>{portfolios?.length === 0 && !debouncedSearch ? "No portfolios yet" : "No portfolios found"}</p>

            {portfolios?.length === 0 && !debouncedSearch && (
              <Button variant="secondary" className={styles["not-found-button"]} onClick={handleCreatePortfolio}>
                Add first portfolio
              </Button>
            )}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Pagination total={totalPages} page={page} onPageChange={handlePageChange} variant="secondary" />
        </div>
      )}
    </div>
  );
};

export default CompanyPortfoliosTab;
